import _ from 'underscore';
import React, { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { tokens } from '@fluentui/react-components';
import {
  CheckmarkCircle20Regular,
  Pause20Regular,
  Circle12Filled,
  Pause12Filled,
} from '@fluentui/react-icons';
import { showPausedCampaignNotifications } from '@bingads-webui-campaign/import-shared-utils';
import { StatusSummaryCard } from '@bingads-webui-campaign-react/status-summary-card';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { useReadCampaign } from './use-read-campaign';

export const CampaignStatusCard: React.FunctionComponent<{ contentReady: Function }> = React.memo(({ contentReady }: { contentReady: Function }) => {
  const i18n = useContext(I18nContext) as any;
  const instrumentation = useContext(InstrumentationContext) as any;
  const { scope } = useContext(ScopeContext) as any;
  const {
    cid, aid, campaignId,
  } = scope.values;

  const log = useMemo(() => {
    if (instrumentation && instrumentation.campaignOverviewEdit) {
      const scenario = instrumentation.campaignOverviewEdit.create();
      return scenario.campaignStatusEdit.create();
    }
    return {
      trace: _.noop,
      error: _.noop,
    };
  }, [instrumentation]);

  const pausedCampaignImportNotificationActivity = useMemo(() => {
    const { activity } = instrumentation.createScenarioAndActivity({
      scenarioName: 'PausedCampaignImportNotification',
      activityName: 'OverviewCampaignStatusCard',
    });
    return activity;
  }, [instrumentation]);

  const menuItemConfigs = useMemo(() => ([
    {
      text: i18n.getString(_TL_('Enabled')),
      value: 'Active',
      icon: Circle12Filled,
      iconColor: tokens.colorPaletteGreenForeground1,
    },
    {
      text: i18n.getString(_TL_('Paused')),
      value: 'Paused',
      icon: Pause12Filled,
    },
  ]), [i18n]);

  const [checkedValue, setCheckedValue] = useState('Active');
  const [campaignItemData, seCampaignItemData] = useState({});
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const set = useCallback((campaign) => {
    setCheckedValue(campaign?.Status === 'Active' ? 'Active' : 'Paused');

    // set campaign item data
    seCampaignItemData(campaign);

    setIsLoading(false);
    contentReady();
  }, [contentReady]);
  const readCampaign = useReadCampaign({ set });
  useEffect(() => {
    setIsLoading(true);
    readCampaign();
  }, [readCampaign, contentReady]);

  const onChange = useCallback((
    e,
    { checkedItems }
  ) => {
    setIsLoading(true);
    edmCampaign
      .Customers.$withKey(cid)
      .Accounts.$withKey(aid)
      .Campaigns.$withKey(campaignId)
      .bulkUpdate({
        Id: campaignId,
        Status: checkedItems[0],
      }).then(() => {
        log.trace(`Campaign status updated to ${checkedItems[0]}`);
        setCheckedValue(checkedItems[0]);

        // show paused campaign notifications based on specific conditions
        showPausedCampaignNotifications({
          campaignItemList: [campaignItemData],
          updateStatus: checkedItems[0] === 'Paused' ? 'UserPaused' : checkedItems[0], // convert the paused status text to be consistent with other scenarios
          activity: pausedCampaignImportNotificationActivity,
        });
      }, (error) => {
        log.error(`Campaign status update failed with error: ${JSON.stringify(error)}`);
      })
      .finally(() => setIsLoading(false));
  }, [cid, aid, campaignId, log, campaignItemData, pausedCampaignImportNotificationActivity]);

  const isActive = checkedValue === 'Active';

  useEffect(() => {
    log.trace('Campaign status card rendered');
  }, [log]);

  return (
    <StatusSummaryCard
      header={i18n.getString(_TL_('Campaign status'))}
      description={_.find(menuItemConfigs, { value: checkedValue })?.text || ''}
      IconComponent={isActive ? CheckmarkCircle20Regular : Pause20Regular}
      color={isActive ? 'green' : 'gray'}
      isLoading={isLoading}
      dropdownMenuProps={{
        checkedValue,
        onChange,
        menuItemConfigs,
        buttonAriaLabel: i18n.getString(_TL_('Edit campaign status')),
      }}
      className="campaign-status-edit-card"
    />
  );
});
